import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import styles from "./RefillQuantity.module.css";
import FixaToggle from "@coworker/apprestructured/src/shared/wrappers/FixaToggle/FixaToggle";
import {
  RefillUnitInputPanel,
  RefillUnitInputPanelProps,
} from "./RefillUnitInputPanel/RefillUnitInputPanel";
import { useTranslation } from "@coworker/locales";
import { RefillUnitType } from "../../constants/RefillUnitType";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { useCallback, useMemo, useState } from "react";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { FixaModalBody } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalBody";
import { FixaModalFooter } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalFooter";
import { FixaInputField } from "@coworker/apprestructured/src/shared/wrappers/FixaInputField/FixaInputField";
import InputWrapper from "../InputWrapper/InputWrapper";
import ErrorAddOn from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/ListItem/ErrorAddOn/ErrorAddOn";
import useSaveGuardedFunction from "@coworker/apprestructured/src/save-guard/useSaveGuardedFunction";

type PanelConfig = {
  label: string;
  panelProps?: RefillUnitInputPanelProps;
  settings?: {
    buttonLabel: string;
    title: string;
    label: string;
    initialValue?: string;
    suffixLabel: string;
    disabled?: boolean;
    onConfirm: (value: string) => Promise<void> | void;
  };
};

type RefillQuantityProps = {
  activeUnit: RefillUnitType;
  onUnitChange: (unit: RefillUnitType) => void;
  showValidation: boolean;
  panelConfigs: Record<RefillUnitType, PanelConfig>;
};

function RefillQuantity({
  activeUnit,
  onUnitChange,
  showValidation,
  panelConfigs,
}: RefillQuantityProps) {
  const [activeSettingsUnit, setActiveSettingsUnit] =
    useState<RefillUnitType | null>(null);
  const [draftValue, setDraftValue] = useState("");
  const [initialDraftValue, setInitialDraftValue] = useState("");

  const { t } = useTranslation();

  const refillUnitTypeValues = Object.values(RefillUnitType);

  const activeSettingsConfig = useMemo(
    () =>
      activeSettingsUnit !== null
        ? panelConfigs[activeSettingsUnit]?.settings
        : undefined,
    [activeSettingsUnit, panelConfigs]
  );

  const handleCloseSettings = useCallback(() => {
    setActiveSettingsUnit(null);
    setDraftValue("");
    setInitialDraftValue("");
  }, []);

  const {
    saveGuardedFunction: handleCloseSettingsWithGuard,
    enableSaveGuard,
    disableSaveGuard,
  } = useSaveGuardedFunction(handleCloseSettings);

  const handleOpenSettings = (unit: RefillUnitType) => {
    const settings = panelConfigs[unit]?.settings;
    if (!settings || settings.disabled) return;

    const nextInitialValue = String(settings.initialValue ?? "");
    setActiveSettingsUnit(unit);
    setDraftValue(nextInitialValue);
    setInitialDraftValue(nextInitialValue);
    disableSaveGuard();
  };

  const handleUnitChange = (unit: RefillUnitType) => {
    handleCloseSettings();
    onUnitChange(unit);
  };

  const handleConfirmSettings = async () => {
    if (!activeSettingsConfig) return;

    const trimmedValue = String(draftValue).trim();
    if (trimmedValue === initialDraftValue.trim()) {
      handleCloseSettings();
      return;
    }

    await activeSettingsConfig.onConfirm(trimmedValue);
    disableSaveGuard();
    handleCloseSettings();
  };

  const sanitizeDigits = (value: string) => value.replace(/[^0-9]/g, "");

  const activeConfig = panelConfigs[activeUnit];

  return (
    <div className={styles["refillQuantity"]}>
      <FixaText textStyle="Heading.XS">{t("refillQuantityString")}</FixaText>
      <FixaToggle
        buttons={refillUnitTypeValues.map((unit) => ({
          text: panelConfigs[unit].label,
        }))}
        activeIndex={refillUnitTypeValues.indexOf(activeUnit)}
        onClick={(_e, index) =>
          handleUnitChange(refillUnitTypeValues[index] as RefillUnitType)
        }
        className={styles["unitToggle"]!}
      />
      {activeConfig?.panelProps ? (
        <RefillUnitInputPanel
          {...activeConfig.panelProps}
          onEditSettings={() => handleOpenSettings(activeUnit)}
        />
      ) : activeConfig?.settings ? (
        <FixaButton
          type="secondary"
          size="small"
          onClick={() => handleOpenSettings(activeUnit)}
          disabled={activeConfig.settings.disabled}
          fluid
        >
          {activeConfig.settings.buttonLabel}
        </FixaButton>
      ) : null}
      {showValidation && (
        <div className={styles["validationError"]}>
          <ErrorAddOn>{t("pleaseFillInString")}</ErrorAddOn>
        </div>
      )}
      <FixaModal
        visible={activeSettingsUnit !== null}
        handleCloseBtn={handleCloseSettingsWithGuard}
      >
        <FixaSheets
          labelledById="refill-quantity-settings-sheet-header"
          alignment="right"
          preserveAlignment={false}
          size="small"
          header={
            <FixaModalHeader
              id="refill-quantity-settings-sheet-header"
              title={activeSettingsConfig?.title ?? ""}
              floating={false}
              closeBtnClick={handleCloseSettingsWithGuard}
            />
          }
          footer={
            <FixaModalFooter>
              <FixaButton type="primary" fluid onClick={handleConfirmSettings}>
                {t("confirmString")}
              </FixaButton>
            </FixaModalFooter>
          }
        >
          <FixaModalBody>
            <InputWrapper>
              <FixaInputField
                id="refill-quantity-settings-input"
                type="text"
                inputMode="numeric"
                label={activeSettingsConfig?.label ?? ""}
                value={draftValue}
                suffixLabel={activeSettingsConfig?.suffixLabel ?? ""}
                onChange={(e) => {
                  const nextValue = sanitizeDigits(e.target.value);
                  setDraftValue(nextValue);
                  if (nextValue !== initialDraftValue) {
                    enableSaveGuard();
                  } else {
                    disableSaveGuard();
                  }
                }}
              />
            </InputWrapper>
          </FixaModalBody>
        </FixaSheets>
      </FixaModal>
    </div>
  );
}

export default RefillQuantity;
